import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { TemplateQuestionLabelOptions } from '@app/types/translatables/template-question-label-display.options';
import _ from 'lodash';

@Component({
    selector: 'app-template-builder',
    templateUrl: './template-builder.template.html',
    styleUrls: ['./template-builder.style.scss'],
})
export class TemplateBuilderComponent implements OnInit, OnChanges, AfterViewInit {
    @Input()
    get fields(): any[] {
        return this._fields;
    }

    set fields(val: any[]) {
        if (val?.length > 0) {
            val.forEach((item) => (item.id = this.generateId()));
        }

        this._fields = val || [];
    }
    @Input() lastSaved: Date;
    @Input() templateName: string;
    @Input() showBuilderImprovements: boolean = false;
    isTemplateModalOpen = false;
    @Output() fieldsChange: EventEmitter<any[]>;
    @Output() autosave = new EventEmitter<any>();
    isActive = false;
    review: any = {};
    templateQuestionLabel = TemplateQuestionLabelOptions;

    toolkit: any[] = [
        { label: 'Freeform Question', icon: 'help', type: 'freeform', isOptional: false },
        { label: 'Rating Question', icon: 'starRating', type: 'rating', isOptional: false },
        { label: 'Agree / Disagree Question', icon: 'thumbsUp', type: 'scale', isOptional: false },
        { label: 'True / False Question', icon: 'checkCircle', type: 'boolean', isOptional: false },
        { label: 'Multiple Choice Question', icon: 'list', type: 'multiple', isOptional: false },
    ];

    _fields: any[] = [];
    private sortIndex: number;

    constructor(
        private element: ElementRef,
        private changeDetector: ChangeDetectorRef
    ) {
        this.fieldsChange = new EventEmitter<any[]>();
    }

    ngOnInit(): void {
        this.renderElements();
    }

    back(): void {
        this.isTemplateModalOpen = false;
        this.exit();
        window.location.reload();
    }

    save(): void {
        this.fieldsChange.emit(this._fields);
        this.autosave.emit();
        this.exit();
    }

    openTemplateModal(): void {
        this.isTemplateModalOpen = true;
    }

    closeTemplateModal(): void {
        this.isTemplateModalOpen = false;
    }

    launch(): void {
        const $container = $('.template-builder-container');
        $container.width($(window).width()).height($(window).height());
        $container.fadeIn(() => {
            this.isActive = true;
        });

        $(window).resize(() => {
            $container.width($(window).width()).height($(window).height());
        });
    }

    exit(): void {
        $('.template-builder-container').fadeOut(() => {
            this.isActive = false;
        });
    }

    deleteField(index: number): void {
        this._fields.splice(index, 1);
        this.redrawFields();
    }

    duplicateField(index: number): void {
        const currentField = _.cloneDeep(this._fields[index - 1]);
        currentField.id = this.generateId();
        this._fields.splice(index, 0, currentField);
        this.redrawFields();
    }

    trackByFn(_index: number, item: any): any {
        return item ? item.id : null;
    }

    trackChoiceIndex(index: any): any {
        return index;
    }

    addChoice(fieldIndex: any): void {
        if (!this.fields[fieldIndex].choices) {
            this.fields[fieldIndex].choices = [];
        }
        this.fields[fieldIndex].choices.push('');
    }

    removeChoice(fieldIndex: any, choiceIndex: any): void {
        this.fields[fieldIndex].choices.splice(choiceIndex, 1);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['fields'] && changes['fields'].currentValue !== changes['fields'].previousValue) {
            this.renderElements();
        }
    }

    ngAfterViewInit(): void {
        $(this.element.nativeElement)
            .find('.available-fields .toolkit-item')
            .draggable({
                connectToSortable: '.sortable.fields',
                cursorAt: { left: 0, top: 0 },
                helper: 'clone',
            });

        $(this.element.nativeElement)
            .find('.sortable.fields')
            .sortable({
                cancel: '[contenteditable], input',
                placeholder: 'toolkit-item-placeholder',
                items: '.toolkit-item',
                tolerance: 'pointer',
                over: () => {
                    $('.emptyLayout').hide();
                },
                out: () => {
                    $('.emptyLayout').show();
                },
                receive: (_event, ui) => {
                    this.addField($(ui.helper).index() - 1, $(ui.helper).attr('field-index'));
                    $(ui.helper).remove();
                },
                start: (_event, ui) => {
                    this.sortIndex = $(ui.item).index() - 1;
                },
                update: (_event, ui) => {
                    if (!ui.item.hasClass('ui-draggable')) {
                        this.sortField(this.sortIndex, $(ui.item).index() - 1);
                        $(this.element.nativeElement).find('.sortable.fields').sortable('cancel');
                    }
                },
            });
    }

    itemMouseUp(e) {
        this.addField(this._fields.length, $(e.target).attr('field-index'));
    }

    private addField(index: number, fieldIndex: number): void {
        const newField = JSON.parse(JSON.stringify(this.toolkit[fieldIndex]));
        newField.id = this.generateId(); // generate a unique id
        if (newField.type === 'multiple') {
            newField.choices = ['', '', ''];
        }
        this._fields.splice(index, 0, newField);
        this.redrawFields();
    }

    private generateId(): string {
        return '_' + Math.random().toString(36).substr(2, 9);
    }

    private sortField(from: any, to: any): void {
        if (to === 0) {
            const element = this._fields.splice(from, 1)[0];
            this._fields.unshift(element);
        } else if (to === this._fields.length - 1) {
            const element = this._fields.splice(from, 1)[0];
            this._fields.push(element);
        } else if (to > from) {
            const element = this._fields.splice(from, 1)[0];
            this._fields.splice(to, 0, element);
        } else if (to < from) {
            const element = this._fields.splice(from, 1)[0];
            this._fields.splice(to, 0, element);
        }
        this.redrawFields();
    }

    private redrawFields(): void {
        this._fields = this._fields.map((val, index) => {
            val.index = index;
            return val;
        });
        this.renderElements();
    }

    private renderRating(): void {
        setTimeout(() => {
            $(this.element.nativeElement).find('.ui.rating').rating();
        });
    }

    private renderElements(): void {
        this.renderRating();
    }
}
